import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  InputGroup,
  InputRightAddon,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormLabelWithInfo } from "../../../../../common/input/FormLabelWithInfo";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  type ProcedureType,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { PROCEDURE_TYPE_OPTIONS } from "../utils/publicProcurementOptions";
import { isTwoStageProcedure } from "../utils/procedure";
import { minCandidatesWhenCapped } from "../utils/karens";
import { CandidateLimitsField } from "../components/CandidateLimitsField";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { SubmissionLanguagesField } from "../components/SubmissionLanguagesField";
import { MilestonePlanCard } from "../components/MilestonePlanCard";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ProcedureStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const { publicContext } = state;
  const validityError = fieldError("publicContext.eFormsNoticeData.tenderValidityMonths");

  const validityMonths = publicContext.eFormsNoticeData?.tenderValidityMonths ?? null;
  const [validityStr, setValidityStr] = useState<string>(validityMonths != null ? String(validityMonths) : "");

  const isSaving = (field: string) => {
    const s = fieldStatus(field);
    return s === "saving" || s === "pending";
  };

  const procedureType = publicContext.procedureType ?? "";
  // Two-stage procedures are del III only — del II's sibling (begrenset tilbudskonkurranse)
  // has different rules and is a separate, later delivery.
  const meetsPartRequirement = (value: ProcedureType) =>
    !isTwoStageProcedure(value) || publicContext.foaPart === "PART_III";
  const optionEnabled = (option: (typeof PROCEDURE_TYPE_OPTIONS)[number]) =>
    option.enabled && meetsPartRequirement(option.value);
  const optionDisabledLabel = (option: (typeof PROCEDURE_TYPE_OPTIONS)[number]) =>
    !option.enabled
      ? t("Coming soon")
      : t("Requires FOA part III (above the EEA threshold)");

  return (
    <Box pb="8" pt="10">
      <FormControl mb="8">
        <FormLabel>
          {t("Procedure type")} {isSaving("publicContext.procedureType") && <Spinner size="xs" />}
        </FormLabel>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
          {PROCEDURE_TYPE_OPTIONS.map((option) => (
            <SelectableCard
              key={option.value}
              isSelected={procedureType === option.value}
              isDisabled={!optionEnabled(option)}
              disabledLabel={optionDisabledLabel(option) ?? undefined}
              onClick={() => {
                if (optionEnabled(option)) {
                  updateField({ publicContext: { procedureType: option.value } });
                }
              }}
            >
              <Text fontWeight="semibold" fontSize="sm" color={option.enabled ? "smText" : "smMuted"}>
                {t(option.value)}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t(option.descriptionKey)}
              </Text>
            </SelectableCard>
          ))}
          <SelectableCard
            key="dynamic-purchasing-system"
            isSelected={false}
            isDisabled
            disabledLabel={t("Coming soon") ?? undefined}
            gridColumn={{ md: "1 / -1" }}
          >
            <Text fontWeight="semibold" fontSize="sm" color="smMuted">
              {t("Dynamic purchasing system (DPS)")}
            </Text>
            <Text fontSize="xs" color="smMuted" mt="1">
              {t("Continuous qualification of suppliers")}
            </Text>
          </SelectableCard>
        </SimpleGrid>
      </FormControl>

      <FormControl
        mb="8"
        isInvalid={!!validityError}
        display={isTwoStageProcedure(publicContext.procedureType) ? "none" : undefined}
      >
        <FormLabelWithInfo
          tooltip={t("Specifies how long suppliers are bound by their bid. Common practice is 3 or 6 months.")}
          suffix={isSaving("publicContext.eFormsNoticeData.tenderValidityMonths") && <Spinner size="xs" ml="1" />}
        >
          {t("Tender validity period")}
        </FormLabelWithInfo>
        <InputGroup maxW="250px">
          <NumberInput
            value={validityStr}
            clampValueOnBlur={false}
            keepWithinRange={false}
            w="full"
            onChange={(valueAsString, valueAsNumber) => {
              setValidityStr(valueAsString);
              if (!isNaN(valueAsNumber)) {
                updateField(
                  { publicContext: { eFormsNoticeData: { tenderValidityMonths: valueAsNumber } } },
                  { debounceMs: 300 },
                );
              }
            }}
          >
            <NumberInputField borderRightRadius="0" />
          </NumberInput>
          {/* t('month_one') t('month_other') */}
          <InputRightAddon color="smMuted" fontSize="sm">
            {t("month", { count: Number(validityStr) || 1 })}
          </InputRightAddon>
        </InputGroup>
        {validityError && (
          <FormErrorMessage>{formatFieldError(validityError)}</FormErrorMessage>
        )}
      </FormControl>

      {/* The stage-1 deadline (frist for forespørsel om å delta) is set and confirmed directly
          on its node in the milestone plan, like the bid deadline for single-stage. */}
      <MilestonePlanCard event={state} />

      {/* Nedvalg follows the plan: first when the request phase ends, then how many proceed. */}
      {isTwoStageProcedure(publicContext.procedureType) && (
        <Box mb="8">
          <CandidateLimitsField
            eventId={eventId}
            current={publicContext.candidateLimits ?? null}
            statutoryMin={minCandidatesWhenCapped(publicContext.foaPart, publicContext.procedureType)}
          />
        </Box>
      )}

      <Box mb="8">
        <SubmissionLanguagesField eventId={eventId} />
      </Box>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
